import apiClient from './client'
import type { AlarmsByType, DashboardSummary, DashboardStation } from '@/types/station'

// shape จริงของ vw_station_summary (ผ่าน /station-summary/) — ยังไม่มี
// power_kw/energy_kwh ในนี้ (คำนวณฝั่ง frontend เอาไว้ทีหลัง)
interface RawStationSummary {
  // ชื่อ field จริงคือ "station" ไม่ใช่ "station_id" — StationSummarySerializer
  // ใช้ fields='__all__' แล้ว field ใน model ชื่อ station (FK) ตรงๆ
  station: number
  station_code: string
  station_name: string
  avg_vol_pn: string
  avg_vol_ll: string
  avg_current: string
  power_factor: string
  frequency: string
  meter_count: number
  status: 'NORMAL' | 'WARNING' | 'ALERT'
  effective_status: 'NORMAL' | 'WARNING' | 'ALERT' | 'OFFLINE'
  is_offline: boolean
  offline_after_seconds: number
  diagnostics_available: boolean
  diagnostics_enabled: boolean
  diagnostics_shadow_mode: boolean
  diagnostic_status: 'CAPABLE' | 'DISABLED' | 'SHADOW' | 'UNAVAILABLE' | 'TOPOLOGY_ERROR'
  diagnostic_topology_error: string | null
  latest_at: string
  // kWh fields (added in migration 0022)
  kwh: string | null
  kwh_midnight: string | null
}

// Connectivity is primary: an offline station must not look like a live alarm.
// `row.status` is retained separately as the last known snapshot severity.
function computeStatus(row: RawStationSummary): DashboardStation['status'] {
  if (row.is_offline || row.effective_status === 'OFFLINE') return 'offline'
  if (row.effective_status === 'ALERT') return 'alert'
  if (row.effective_status === 'WARNING') return 'warning'
  return 'active' // NORMAL
}

function computeLastKnownStatus(row: RawStationSummary): DashboardStation['last_known_status'] {
  if (row.status === 'ALERT') return 'alert'
  if (row.status === 'WARNING') return 'warning'
  return 'active'
}

function toDashboardStation(row: RawStationSummary): DashboardStation {
  // คำนวณ energy_today = kwh_now - kwh_midnight
  const kwhNow = Number(row.kwh) || 0
  const kwhMidnight = Number(row.kwh_midnight) || 0
  const energyKwh = Math.max(0, kwhNow - kwhMidnight) // ป้องกันค่าติดลบ

  return {
    station_id: row.station,
    station_code: row.station_code,
    station_name: row.station_name,
    power_kw:   Math.round(((Math.sqrt(3) * Number(row.avg_vol_ll) * Number(row.avg_current) * Number(row.power_factor)) / 1000) * 100 ) / 100,
    voltage_ll: Math.round(Number(row.avg_vol_ll) * 100) / 100,
    voltage_pn: Math.round(Number(row.avg_vol_pn) * 100) / 100,
    current_avg: Math.round(Number(row.avg_current) * 100) / 100,
    pf: Math.round(Number(row.power_factor) * 100) / 100,
    status: computeStatus(row),
    last_known_status: computeLastKnownStatus(row),
    last_reading: row.latest_at,
    meter_count: row.meter_count,
    diagnostic_status: row.diagnostic_status,
    diagnostic_topology_error: row.diagnostic_topology_error,
    energy_kwh: Math.round(energyKwh * 100) / 100,
  }
}

// TODO: ยังไม่มี /dashboard/summary/ จริงบน backend (ต้อง aggregate จาก
// หลายตาราง) — เก็บ endpoint เดิมไว้เผื่ออนาคต แต่ store จะไม่เรียกตอนนี้
export async function getSummary(): Promise<DashboardSummary> {
  const response = await apiClient.get<DashboardSummary>('/dashboard/summary/')
  return response.data
}

// /station-summary/ ไม่ paginate ฝั่ง backend แล้ว (pagination_class = None)
// เพราะจำนวน station ทั้งหมดไม่มาก เลย fetch มาทั้งหมดในครั้งเดียว —
// การนับ active/offline หรือแบ่งหน้าแสดงผล ให้ทำที่ frontend เอาจาก list นี้
export async function getStations(): Promise<DashboardStation[]> {
  const response = await apiClient.get<RawStationSummary[]>('/station-summary/')
  return response.data.map(toDashboardStation)
}

interface StatusSummaryBreakdownItem {
  alarm_type_id: number
  alarm_code: string
  alarm_name: string
  station_count: number
}

interface StatusSummaryResponse {
  total_stations: number
  stations_alert: number
  stations_warning: number
  stations_offline: number
  stations_normal: number
  stations_offline_total: number
  stations_online: number
  total_stations_with_alarm: number
  breakdown: StatusSummaryBreakdownItem[]
}

// สรุปสถานะสถานีจาก tb_station_state (ground truth) — ผ่าน /station-status-summary/
// นับ total/alert/warning/normal ต่อสถานี + breakdown ราย alarm type สำหรับ donut
//
// OFFLINE is one of the mutually exclusive primary buckets. The legacy
// stations_offline_total field is kept for API compatibility and now has the
// same value. Alarm breakdowns can still include an offline station's stale
// last-known alarm.
export interface StationStatusSummary {
  total_stations: number
  stations_alert: number
  stations_warning: number
  stations_offline: number
  stations_normal: number
  stations_offline_total: number
  stations_online: number
  total_stations_with_alarm: number
  by_type: AlarmsByType
}

export async function getStationStatusSummary(): Promise<StationStatusSummary> {
  const response = await apiClient.get<StatusSummaryResponse>('/station-status-summary/')
  const d = response.data
  const byType: AlarmsByType = { gfr: 0, ovpr: 0, uvpr: 0 }
  for (const item of d.breakdown) {
    const key = item.alarm_code.toLowerCase()
    if (key === 'gfr' || key === 'ovpr' || key === 'uvpr') {
      byType[key] = item.station_count
    }
  }
  return {
    total_stations: d.total_stations,
    stations_alert: d.stations_alert,
    stations_warning: d.stations_warning,
    stations_offline: d.stations_offline,
    stations_normal: d.stations_normal,
    stations_offline_total: d.stations_offline_total,
    stations_online: d.stations_online,
    total_stations_with_alarm: d.total_stations_with_alarm,
    by_type: byType,
  }
}
